/**
 * usePlanPhaseExecution — Group B from usePlanOrchestration.
 *
 * Phase execution engine:
 *   advanceToPhaseDecomposition, executePhase, executeAllPhases, executePlan,
 *   pauseExecution, retryFromStage
 */
import { useCallback } from 'react';
import type React from 'react';
import { usePlanStore } from '@/stores/ai/plan-store';
import { useDebugStore } from '@/stores/ai/debug-store';
import { planApi } from '@/services/api/agentApi';
import {
  topologicalSort,
  enrichExecutionPrompt,
  routeToVisualEditorStreaming,
} from '@/services/editor/AgentConversationManager';
import {
  validateInteractionPromptReferences,
  validateInteractionPhaseDependencies,
} from '@/services/editor/planPromptValidation';
import { canonicalizePublishedVisualComponentArtifacts } from '@/services/editor/planArtifactCanonicalization';
import {
  applyFinalAuditScoreCaps,
  buildFinalAuditReadiness,
  buildSyntheticIncompleteFinalAudit,
} from '@/services/editor/planFinalAuditPolicy';
import {
  buildPersistentInteractionPhaseFailureOutcome,
  getPersistentInteractionVerificationFailure,
} from '@/services/editor/planInteractionVerificationPolicy';
import {
  replaceStepVerificationIssues,
  type CollectedVerificationIssue,
} from '@/services/editor/verificationIssueCollection';
import { decidePhaseRetry } from '@/services/editor/planRetryPolicy';
import { EditorContextSerializer } from '@/services/editor/EditorContextSerializer';
import { formatPhase11BBindingInstructionsForPrompt } from '@/services/ai/BindingContextSummary';
import { useEditorStore } from '@/stores/editor/editor-store';
import { PageTemplate } from '@/types';
import type { AuditResult, AuditCriterion, PlanPipelineStage } from '@/types/plan';
import type { PlanOrchestrationDeps } from './usePlanOrchestration';
import { isAbortError, toErrMsg, captureDebugPrompts, asRecord, asRecordArray } from './planHelpers';
import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────────────
// Interfaces
// ─────────────────────────────────────────────────────────────────────────────

export interface PhaseExecutionOutput {
  artifacts: Record<string, string[]>;
  hadStreamingErrors: boolean;
  liaisonValidation: { passed: boolean; issues: string[] } | null;
  collectedVerificationIssues: CollectedVerificationIssue[];
  unresolvedTokens: string[];
  enrichedPrompt: string;
}

export interface PhaseExecutionResult {
  advanceToPhaseDecomposition: () => Promise<void>;
  executePhase: (phaseId: string, overridePrompt?: string) => Promise<PhaseExecutionOutput>;
  executeAllPhases: () => Promise<void>;
  executePlan: () => Promise<void>;
  pauseExecution: () => void;
  retryFromStage: (stage: PlanPipelineStage) => Promise<void>;
}

export interface PhaseExecutionRefs {
  abortControllerRef: React.MutableRefObject<AbortController | null>;
  accumulatedArtifacts: React.MutableRefObject<Record<string, Record<string, string[]>>>;
  accumulatedFeedback: React.MutableRefObject<string[]>;
  executionStartTimeRef: React.MutableRefObject<number>;
  isPausedRef: React.MutableRefObject<boolean>;
}

const AuditActionSchema = z.record(z.string(), z.unknown());
const TemplateDiagnosticsSchema = z.object({
  skippedSchemas: z.array(z.string()).optional(),
  failedTemplates: z.array(z.unknown()).optional(),
}).passthrough();

function parseAuditAction(rawAction: string): Record<string, unknown> {
  try {
    const raw: unknown = JSON.parse(rawAction);
    const result = AuditActionSchema.safeParse(raw);
    return result.success ? result.data : { raw: rawAction };
  } catch {
    return { raw: rawAction };
  }
}

function parseTemplateDiagnostics(raw: unknown): z.infer<typeof TemplateDiagnosticsSchema> | null {
  const result = TemplateDiagnosticsSchema.safeParse(raw);
  return result.success ? result.data : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Local helpers
// ─────────────────────────────────────────────────────────────────────────────

export interface Phase11BAutoDataBindingPromptInput {
  schemaContext: string;
  queryTemplateContext: string;
  hasSchemaGroups: boolean;
}

export function buildPhase11BAutoDataBindingPrompt({
  schemaContext,
  queryTemplateContext,
  hasSchemaGroups,
}: Phase11BAutoDataBindingPromptInput): string {
  const promptParts = [
    'Bind all unbound chart/table/PM data-capable components to the data schemas created in the prior phase.',
    `[DATA SCHEMAS — use these exact IDs for all data bindings]
${schemaContext}`,
  ];

  if (queryTemplateContext) {
    promptParts.push(
      `[QUERY TEMPLATES — use full query-template view identity for matching bindings]
${queryTemplateContext}`,
      `When a template's measures match a component's needs, bind through properties.dataBinding.source with kind="query-template", viewId, ownerType, ownerId, and viewOrganizationId from the matching view summary, plus properties.dataBinding.fieldMapping for component fields.`,
    );
  }

  if (hasSchemaGroups) {
    promptParts.push(
      'Schema read bindings must use properties.dataBinding.source with kind="schema", dataOwnerOrganizationId, ownerType, ownerId, groupId, and schemaId from the chosen catalog source summary.',
      'Do not persist Phase 11B read bindings through legacy dataSource, queryDefinition, template-id-only, or composite-query fields.',
    );
  }

  promptParts.push(formatPhase11BBindingInstructionsForPrompt());
  return promptParts.join('\n\n');
}

/** Extract top-level changed keys from an action's changes object (dot-notation for shallow nesting). */
function extractChangedKeys(changes: Record<string, unknown>, prefix = ''): string[] {
  const keys: string[] = [];
  for (const [key, val] of Object.entries(changes)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    const nested = asRecord(val);
    if (nested && Object.keys(nested).length <= 5) {
      keys.push(...extractChangedKeys(nested, fullKey));
    } else {
      keys.push(fullKey);
    }
  }
  return keys.slice(0, 15);
}

/** Build a concise summary of an action's payload for debug visibility. */
function summarizeAction(action: Record<string, unknown>, index: number): {
  index: number; type: string; componentId: string; componentName?: string; changedKeys: string[];
} {
  const type = typeof action.type === 'string' ? action.type : 'unknown';
  const payload = asRecord(action.payload) ?? {};

  if (type === 'update_component') {
    const changesObj = asRecord(payload.changes);
    return {
      index, type,
      componentId: typeof payload.componentId === 'string' ? payload.componentId : '',
      changedKeys: changesObj ? extractChangedKeys(changesObj) : [],
    };
  }
  if (type === 'add_component') {
    const comp = asRecord(payload.component) ?? {};
    const props = asRecord(comp.properties) ?? {};
    const categories: string[] = [typeof comp.type === 'string' ? comp.type : 'unknown'];
    if (Array.isArray(props.interactions) && props.interactions.length) categories.push(`+interactions(${props.interactions.length})`);
    if (props.validation) categories.push('+validation');
    if (props.dataSource) categories.push('+dataSource');
    const style = asRecord(comp.style) ?? {};
    if (style.transition || style.animation) categories.push('+animation');
    if (props.modal) categories.push('+modal');
    return {
      index, type,
      componentId: typeof comp.id === 'string' ? comp.id : '',
      componentName: typeof comp.name === 'string' ? comp.name : undefined,
      changedKeys: categories,
    };
  }
  return { index, type, componentId: '', changedKeys: [] };
}

/** Enhancement A: Validate that query templates created in the data phase align with what the prompt expected. */
function validateSchemaQueryAlignment(
  enrichedPrompt: string,
  artifacts: Record<string, unknown[]>,
): import('@/stores/ai/debug-store').SchemaQueryAlignment | null {
  // Parse expected queries from the enriched prompt's "## Query Templates" section
  const querySection = enrichedPrompt.match(/##\s*(?:Create\s+)?Query Templates[\s\S]*?(?=\n##\s|\n#\s|$)/i);
  if (!querySection) return null;

  const expectedQueries: Array<{ name: string; targetSchema: string }> = [];
  // Match patterns like: **latest-weight** — schema: weight_entries, filter...
  // or: - latestWeight: weight_entries, filter/sort/limit...
  // Uses [\w-]+ to match hyphenated names like "latest-weight", "chart-data"
  const patterns = [
    /\*\*([\w-]+)\*\*\s*(?:—\s*(?:schema:\s*)?)?:?\s*([\w-]+)/g,
    /[-*]\s*\*\*([\w-]+)\*\*\s*(?:—\s*(?:schema:\s*)?)?:?\s*([\w-]+)/g,
    /[-*]\s*([\w-]+)\s*:\s*([\w-]+)\s*,\s*(?:filter|sort|limit|query|aggregate)/gi,
  ];
  const seen = new Set<string>();
  for (const pattern of patterns) {
    let match;
    while ((match = pattern.exec(querySection[0])) !== null) {
      const key = `${match[1]}:${match[2]}`.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        expectedQueries.push({ name: match[1], targetSchema: match[2] });
      }
    }
  }
  if (expectedQueries.length === 0) return null;

  // Parse actual query templates: "name(templateId:xxx,schemaId:yyy[,organizationId:zzz])"
  const actualQueries: Array<{ name: string; templateId: string; schemaId: string }> = [];
  for (const ctx of (artifacts.queryTemplateContext || [])) {
    if (typeof ctx !== 'string') continue;
    const m = ctx.match(/^(.+?)\(templateId:([^,]+),schemaId:([^,)]+)(?:,organizationId:([^)]*))?\)$/);
    if (m) actualQueries.push({ name: m[1], templateId: m[2], schemaId: m[3] });
  }

  // Parse schemas: "name(id:xxx,group:yyy,fields:[...])"
  const schemas: Array<{ name: string; schemaId: string }> = [];
  for (const ctx of (artifacts.schemaContext || [])) {
    if (typeof ctx !== 'string') continue;
    const m = ctx.match(/^(.+?)\(id:([^,]+),/);
    if (m) schemas.push({ name: m[1], schemaId: m[2] });
  }

  // Strip common project prefix from schema names for matching (e.g., "p69cf1ac0_weight_entries" → "weight_entries")
  const stripPrefix = (name: string) => name.replace(/^p[a-f0-9]+_/i, '');

  // Cross-reference
  const queryAlignments = expectedQueries.map(eq => {
    const targetSchemaObj = schemas.find(s =>
      stripPrefix(s.name).toLowerCase() === eq.targetSchema.toLowerCase(),
    );
    const matchedTemplate = actualQueries.find(aq =>
      targetSchemaObj ? aq.schemaId === targetSchemaObj.schemaId : false,
    );
    return {
      expectedQueryName: eq.name,
      targetSchema: eq.targetSchema,
      matched: !!matchedTemplate,
      matchedTemplateName: matchedTemplate?.name,
      matchedTemplateId: matchedTemplate?.templateId,
    };
  });

  const schemaAlignments = schemas.map(s => {
    const stripped = stripPrefix(s.name).toLowerCase();
    const expectedCount = expectedQueries.filter(eq => eq.targetSchema.toLowerCase() === stripped).length;
    const actualForSchema = actualQueries.filter(aq => aq.schemaId === s.schemaId);
    return {
      schemaName: s.name,
      schemaId: s.schemaId,
      expectedQueryCount: expectedCount,
      actualQueryCount: actualForSchema.length,
      matchedQueries: actualForSchema.map(aq => aq.name),
    };
  });

  const issues: string[] = [];
  const unmatched = queryAlignments.filter(qa => !qa.matched);
  if (unmatched.length > 0) {
    issues.push(`${unmatched.length}/${queryAlignments.length} expected queries have no matching template: ${unmatched.map(q => `${q.expectedQueryName} (${q.targetSchema})`).join(', ')}`);
  }
  for (const sa of schemaAlignments) {
    if (sa.expectedQueryCount > 0 && sa.actualQueryCount === 0) {
      issues.push(`Schema "${sa.schemaName}" expected ${sa.expectedQueryCount} queries but got 0`);
    }
  }
  const schemaNames = new Set(schemas.map(s => stripPrefix(s.name).toLowerCase()));
  for (const eq of expectedQueries) {
    if (!schemaNames.has(eq.targetSchema.toLowerCase())) {
      issues.push(`Expected query "${eq.name}" targets schema "${eq.targetSchema}" which was not created`);
    }
  }

  // Compute alignment before surplus (surplus is informational, not a blocking issue)
  const aligned = issues.length === 0;

  // Enhancement H: Detect surplus query templates (more created than requested)
  for (const sa of schemaAlignments) {
    if (sa.expectedQueryCount > 0 && sa.actualQueryCount > sa.expectedQueryCount) {
      issues.push(`Schema "${sa.schemaName}" has ${sa.actualQueryCount - sa.expectedQueryCount} surplus template(s) (expected ${sa.expectedQueryCount}, got ${sa.actualQueryCount}): ${sa.matchedQueries.join(', ')}`);
    }
  }

  return { schemaAlignments, queryAlignments, issues, aligned };
}

function dedupeExecutionIssues(issues: string[]): string[] {
  return Array.from(new Set(issues.map((issue) => issue.trim()).filter(Boolean)));
}

function applyDeterministicExecutionDegradation(params: {
  auditResult: AuditResult;
  unresolvedTokens: string[];
  verificationIssues: Array<{ step: string; severity: string; category: string; message: string }>;
}): AuditResult {
  const { auditResult, unresolvedTokens, verificationIssues } = params;

  const degradationIssues = dedupeExecutionIssues([
    unresolvedTokens.length > 0
      ? `Phase executed in degraded mode due to unresolved execution tokens: ${unresolvedTokens.join(', ')}`
      : '',
    ...verificationIssues
      .filter((issue) => issue.severity === 'warning' || issue.severity === 'error')
      .map((issue) => `[${issue.step}/${issue.category}] Deterministic verification ${issue.severity}: ${issue.message}`),
  ]);

  if (degradationIssues.length === 0) {
    return auditResult;
  }

  const nextWarnings = dedupeExecutionIssues([
    ...(auditResult.warnings ?? []),
    ...degradationIssues,
  ]);

  const degradedAudit: AuditResult = {
    ...auditResult,
    warnings: nextWarnings,
  };

  if (degradedAudit.verdict === 'pass' && degradedAudit.overallScore > 79) {
    degradedAudit.overallScore = 79;
    degradedAudit.verdict = 'warn';
    degradedAudit.warnings = dedupeExecutionIssues([
      ...nextWarnings,
      'Deterministic degradation cap applied: phase executed with unresolved prompt context or verification issues, so it cannot receive a clean PASS.',
    ]);
  }

  return degradedAudit;
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function usePlanPhaseExecution(
  deps: PlanOrchestrationDeps,
  refs: PhaseExecutionRefs,
  advanceStage: (stage: PlanPipelineStage) => void,
  _addUIMessage: (text: string, role: 'assistant' | 'user', meta?: Record<string, unknown>) => void,
): PhaseExecutionResult {
  const planStore = usePlanStore();
  const debug = useDebugStore;

  const {
    abortControllerRef,
    accumulatedArtifacts,
    accumulatedFeedback,
    executionStartTimeRef,
    isPausedRef,
  } = refs;

  const debugEvent = useCallback((type: string, data: unknown) => {
    debug.getState().pushEvent({ type, data, timestamp: Date.now() });
  }, [debug]);

  const planQualityGate = useCallback((gate: {
    stage: string;
    phaseId?: string;
    verdict: string;
    score?: number;
    action: string;
    retryNumber?: number;
  }) => {
    const ds = debug.getState();
    if (ds.isEnabled && ds.planDebugData) {
      ds.recordPlanQualityGate(gate);
    }
  }, [debug]);

  // ── advanceToPhaseDecomposition ────────────────────────────────────────────

  const advanceToPhaseDecomposition = useCallback(async () => {
    if (!planStore.roadmap) {
      planStore.setError('No roadmap available');
      advanceStage('failed');
      return;
    }
    const roadmap = planStore.roadmap;

    const controller = new AbortController();
    abortControllerRef.current = controller;
    planStore.setAbortController(controller);
    const signal = controller.signal;

    try {
      const isDebug = debug.getState().isEnabled;

      // Decompose
      advanceStage('decomposing');
      planStore.setDecomposing(true);
      const decomposeStart = Date.now();
      debugEvent('step_start', { step: 'plan_phase_decompose', agent: 'PhaseDecompositionService' });
      const decomposed = await planApi.decomposePhases(
        roadmap,
        planStore.researchContext ?? undefined,
        signal,
        isDebug,
      );
      planStore.incrementLLMCalls();
      captureDebugPrompts(decomposed, 'decompose');
      planStore.setDecomposing(false);
      if (!decomposed.data?.phases) {
        planStore.setError('Phase decomposition returned no phases');
        advanceStage('failed');
        return;
      }
      const phases = decomposed.data.phases;
      planStore.setPhases(phases);
      debugEvent('step_complete', { step: 'plan_phase_decompose', duration_ms: Date.now() - decomposeStart, phaseCount: phases.length });

      // Audit phases
      advanceStage('auditing_phases');
      planStore.setAuditingPhases(true);
      const phaseAuditStart = Date.now();
      debugEvent('step_start', { step: 'plan_phase_audit', agent: 'PlanAuditService' });
      const audited = await planApi.auditPhases(phases, roadmap, signal, isDebug);
      planStore.incrementLLMCalls();
      captureDebugPrompts(audited, 'phase_audit');
      if (audited.data?.phaseAudits) {
        const MAX_PHASE_PROMPT_RETRIES = 2;

        for (const [phaseId, audit] of Object.entries(audited.data.phaseAudits)) {
          let currentAudit = audit;
          let phaseRetries = 0;

          while (currentAudit.verdict !== 'pass' && phaseRetries < MAX_PHASE_PROMPT_RETRIES) {
            phaseRetries++;
            debugEvent('quality_gate', {
              stage: 'plan_phase_audit', phaseId,
              verdict: currentAudit.verdict, action: 'retry',
              retryNumber: phaseRetries, score: currentAudit.overallScore,
            });
            planQualityGate({
              stage: 'auditing_phases', phaseId,
              verdict: currentAudit.verdict, action: 'retry',
              retryNumber: phaseRetries,
            });

            try {
              // Regenerate the failed phase prompt using audit feedback
              const regenResult = await planApi.regeneratePhase(
                phaseId, currentAudit, roadmap, signal, isDebug,
              );
              planStore.incrementLLMCalls();
              captureDebugPrompts(regenResult, 'phase_regenerate', phaseId);

              // Update the phase in the store with the regenerated prompt
              if (regenResult.data?.phase) {
                const currentPhases = [...planStore.phases];
                const idx = currentPhases.findIndex(p => p.id === phaseId);
                if (idx !== -1) {
                  currentPhases[idx] = { ...currentPhases[idx], executionPrompt: regenResult.data.phase.executionPrompt };
                  planStore.setPhases(currentPhases);
                }
              }

              // Re-audit this phase (read fresh from store after update)
              const phaseToAudit = planStore.phases.find(p => p.id === phaseId);
              if (!phaseToAudit) {
                console.warn(`[Plan] Phase ${phaseId} not found in phases array, skipping re-audit`);
                break;
              }
              const reAudited = await planApi.auditPhases(
                [phaseToAudit], roadmap, signal, isDebug,
              );
              planStore.incrementLLMCalls();
              captureDebugPrompts(reAudited, 'phase_audit', phaseId);

              if (reAudited.data?.phaseAudits?.[phaseId]) {
                currentAudit = reAudited.data.phaseAudits[phaseId];
              } else {
                console.warn(`[Plan] Re-audit for ${phaseId} returned no matching result, breaking retry loop`);
                break;
              }
            } catch (retryErr) {
              console.error(`[Plan] Phase ${phaseId} retry ${phaseRetries} failed:`, retryErr);
              debugEvent('error', { stage: 'plan_phase_retry', phaseId, retry: phaseRetries, error: toErrMsg(retryErr) });
              break; // Don't stall — exit retry loop and continue with remaining phases
            }
          }

          planStore.setPhaseAudit(phaseId, currentAudit);
        }
      }

      planStore.setAuditingPhases(false);
      debugEvent('step_complete', { step: 'plan_phase_audit', duration_ms: Date.now() - phaseAuditStart });

      // Checkpoint 2
      advanceStage('reviewing');
    } catch (err: unknown) {
      planStore.setDecomposing(false);
      planStore.setAuditingPhases(false);
      if (!isAbortError(err)) {
        debugEvent('error', { stage: 'plan_decompose', error: toErrMsg(err) });
        planStore.setError(toErrMsg(err));
        advanceStage('failed');
      }
    }
  }, [abortControllerRef, advanceStage, debug, debugEvent, planQualityGate, planStore]);

  // ── executePhase ───────────────────────────────────────────────────────────

  const executePhase = useCallback(async (
    phaseId: string,
    overridePrompt?: string,
  ): Promise<{ artifacts: Record<string, string[]>; hadStreamingErrors: boolean; liaisonValidation: { passed: boolean; issues: string[] } | null; collectedVerificationIssues: CollectedVerificationIssue[]; unresolvedTokens: string[]; enrichedPrompt: string }> => {
    // Look up phase/section
    const phase = planStore.phases.find(p => p.id === phaseId)
      || planStore.plan?.sections.find(s => s.id === phaseId);
    if (!phase) throw new Error(`Phase/section ${phaseId} not found`);
    const domain = phase.domain || 'visual-editor';

    // Use getState() for editedPhasePrompts to avoid stale closure reads
    const currentEdited = usePlanStore.getState().editedPhasePrompts[phaseId];
    const rawPrompt = overridePrompt
      || currentEdited
      || phase.executionPrompt
      || phase.prompt   // backward compat: old backend field name
      || '';
    const promptSource = overridePrompt ? 'override' : currentEdited ? 'editedPhasePrompts' : phase.executionPrompt ? 'executionPrompt' : phase.prompt ? 'prompt(legacy)' : 'empty';
    console.info(`[Plan:executePhase] ${phaseId} prompt source: ${promptSource}`, { promptPreview: rawPrompt.slice(0, 120) });

    // Strip hallucinated agent system prompt prefixes from decomposed phase prompts
    const SYSTEM_PROMPT_PREFIXES = [
      /^You are the TerraMatrix (?:visual editor agent|LiaisonAgent|Styles Agent|Layout Agent|Component Agent)[^.]*\.\s*/i,
      /^You are the TerraMatrix [a-zA-Z]+ (?:agent|service)[^.]*\.\s*/i,
    ];
    let cleanedPrompt = rawPrompt;
    for (const prefix of SYSTEM_PROMPT_PREFIXES) {
      cleanedPrompt = cleanedPrompt.replace(prefix, '');
    }
    if (cleanedPrompt !== rawPrompt) {
      console.info('[Plan] Stripped system prompt prefix from phase', phaseId);
    }

    let { enrichedPrompt, unresolvedTokens } = enrichExecutionPrompt(
      cleanedPrompt,
      accumulatedArtifacts.current,
      { domain },
    );
    if (unresolvedTokens.length > 0) {
      console.warn('[Plan] Unresolved artifact tokens:', unresolvedTokens);
      debugEvent('quality_gate', {
        stage: 'enrichment_resolution', phaseId,
        verdict: 'warn', action: 'continue_degraded',
        unresolvedTokens,
      });
      planQualityGate({
        stage: 'executing_phases', phaseId,
        verdict: 'warn', action: 'continue_degraded',
      });
      // Replace the misleading [NOTE: ...] placeholders with actionable guidance
      enrichedPrompt += `\n\n[WARNING: The following artifact references could not be resolved from prior phases: ${unresolvedTokens.join(', ')}. Generate components without data binding for these references — they will be bound in a later phase or manually.]`;
    }

    if (!enrichedPrompt.trim()) {
      enrichedPrompt = `Phase ${phase.phaseNumber ?? ''}: ${phase.title || phaseId}. Domain: ${phase.domain || 'visual-editor'}. Execute this phase of the plan.`;
      console.warn('[Plan] Empty executionPrompt for phase', phaseId, '— using fallback prompt');
    }

    if (domain === 'interaction') {
      const interactionValidation = validateInteractionPromptReferences(
        cleanedPrompt || rawPrompt || '',
        accumulatedArtifacts.current,
      );

      if (interactionValidation.warnings.length > 0 || !interactionValidation.valid) {
        if (interactionValidation.warnings.length > 0) {
          console.warn('[Plan] Interaction prompt validation warnings:', interactionValidation.warnings);
        }
        if (!interactionValidation.valid) {
          // Downgraded from block to warn: the compilation prompt may generate
          // descriptive IDs that don't match actual component IDs, but the
          // enrichment system injects a [COMPONENT MAP] with real IDs so the
          // interaction agent can resolve them. Hard-blocking here prevents
          // the interaction phase from running at all.
          console.warn('[Plan] Interaction prompt has unresolved references (non-blocking):', interactionValidation.invalidReferences);
          enrichedPrompt += `\n\n[NOTE: The following component references in this prompt may not match actual IDs. Use the COMPONENT MAP above to find the correct IDs by matching component purpose/name: ${interactionValidation.invalidReferences.join(', ')}]`;
        }
        debugEvent('quality_gate', {
          stage: 'interaction_prompt_validation',
          phaseId,
          verdict: 'warn',
          action: 'continue',
          warnings: interactionValidation.warnings,
          invalidReferences: interactionValidation.invalidReferences,
        });
        planQualityGate({
          stage: 'executing_phase',
          phaseId,
          verdict: 'warn',
          action: 'continue',
        });
      }

      const dependencyValidation = validateInteractionPhaseDependencies(
        cleanedPrompt || rawPrompt || '',
        accumulatedArtifacts.current,
      );

      if (dependencyValidation.warnings.length > 0 || !dependencyValidation.valid) {
        if (dependencyValidation.warnings.length > 0) {
          console.warn('[Plan] Interaction dependency validation warnings:', dependencyValidation.warnings);
        }
        if (!dependencyValidation.valid) {
          console.warn('[Plan] Interaction dependency validation failed:', dependencyValidation.blockingIssues);
        }
        debugEvent('quality_gate', {
          stage: 'interaction_dependency_validation',
          phaseId,
          verdict: dependencyValidation.valid ? 'warn' : 'fail',
          action: dependencyValidation.valid ? 'continue' : 'block_execution',
          warnings: dependencyValidation.warnings,
          blockingIssues: dependencyValidation.blockingIssues,
        });
        planQualityGate({
          stage: 'executing_phase',
          phaseId,
          verdict: dependencyValidation.valid ? 'warn' : 'fail',
          action: dependencyValidation.valid ? 'continue' : 'block_execution',
        });
      }

      if (!dependencyValidation.valid) {
        // Downgraded from hard-block to warning: the Data Component Agent may
        // convert text→data-text via update_component after the componentSummary
        // is built, causing false negatives in KPI/chart/table counts. The
        // interaction agent receives the real COMPONENT MAP from enrichment, so
        // it can still resolve components correctly.
        console.warn('[Plan] Interaction dependency validation failed (non-blocking):', dependencyValidation.blockingIssues);
        enrichedPrompt += `\n\n[NOTE: Dependency validation flagged missing component types: ${dependencyValidation.blockingIssues.join('; ')}. Use the COMPONENT MAP above to verify actual component availability.]`;
        planQualityGate({
          stage: 'executing_phase',
          phaseId,
          verdict: 'warn',
          action: 'continue',
        });
      }
    }

    // Opt 7: Inject accumulated audit feedback from prior phases
    if (accumulatedFeedback.current.length > 0) {
      enrichedPrompt += `\n\n[PRIOR PHASE FEEDBACK — avoid these issues]\n${accumulatedFeedback.current.join('\n')}`;
    }

    const artifacts: Record<string, string[]> = {};
    let hadStreamingErrors = false;

    const isDebug = debug.getState().isEnabled;
    if (isDebug) {
      debug.getState().initPhaseExecution(phaseId, phase.title || phaseId, domain, enrichedPrompt);
      // Debug 6: Track unresolved enrichment tokens
      if (unresolvedTokens.length > 0) {
        debug.getState().setPhaseUnresolvedTokens(phaseId, unresolvedTokens);
      }
    }

    const editorCtx = EditorContextSerializer.serialize();
    const collectedComponentIds: string[] = [];
    const collectedComponentInfo: Array<{ id: string; type: string; name: string }> = [];
    const collectedPageIds: string[] = [];
    const collectedActions: unknown[] = [];
    const collectedVerificationIssues: CollectedVerificationIssue[] = [];
    const stepStartTimes: Record<string, number> = {};
    let liaisonValidation: { passed: boolean; issues: string[] } | null = null;

    // Extract pageName for multi-page plans (from PhasePrompt or PlanSection)
    const pageName = phase.pageName;

    if (domain === 'workflow' || domain === 'data') {
      // Workflow/data phases route via LiaisonAgent → domain agent (MQTT)
      const liaisonResult = await planApi.executePhaseLiaison(
        phaseId, enrichedPrompt, domain,
        editorCtx, planStore.currentThreadId ?? undefined,
        abortControllerRef.current?.signal,
      );
      planStore.incrementLLMCalls();

      // Debug: log liaison response shape to verify DataDomainAgent schema creation
      console.info('[Plan] Liaison result for domain:', domain, {
        hasData: !!liaisonResult?.data,
        hasMetadata: !!liaisonResult?.data?.metadata,
        schemaCount: liaisonResult?.data?.metadata?.schemas?.length ?? 0,
        responseKeys: Object.keys(liaisonResult?.data || {}),
      });

      // Extract data artifacts from liaison response (check metadata for MQTT-routed responses)
      const responseData = liaisonResult?.data || liaisonResult;
      const dataArtifacts = asRecord(asRecord(responseData)?.metadata) ?? asRecord(responseData);
      const schemas = asRecordArray(dataArtifacts?.schemas);
      const queryTemplates = asRecordArray(dataArtifacts?.queryTemplates);
      const bindings = asRecordArray(dataArtifacts?.bindings);
      if (schemas.length > 0) {
        artifacts.schemaIds = schemas.map((s) => {
          const raw = s.schemaId ?? s.id ?? s.name ?? s._id;
          return typeof raw === 'string' ? raw : String(raw ?? '');
        });
      }
      if (queryTemplates.length > 0) {
        artifacts.queryIds = queryTemplates.map((q) => {
          const raw = q.templateId ?? q.id ?? q._id;
          return typeof raw === 'string' ? raw : String(raw ?? '');
        }).filter(Boolean);
        // Store rich template context for cross-phase enrichment (name, templateId, schemaId)
        // so the DataComponentAgent can reference pre-built templates instead of re-inventing inline queries
        artifacts.queryTemplateContext = queryTemplates
          .filter((q) => q.templateId || q.id || q._id)
          .map((q) => {
            const tid = q.templateId || q.id || q._id;
            const name = q.name || 'unnamed';
            const sid = q.schemaId || '';
            const orgId = typeof q.organizationId === 'string' && q.organizationId.trim()
              ? q.organizationId.trim()
              : '';
            return `${name}(templateId:${tid},schemaId:${sid}${orgId ? `,organizationId:${orgId}` : ''})`;
          });
      }
      if (bindings.length > 0) {
        artifacts.bindingIds = bindings.map((b) => {
          const raw = b.id ?? b._id;
          return typeof raw === 'string' ? raw : String(raw ?? '');
        });
      }

      // Surface query template diagnostics if present (skipped/failed schemas)
      if (dataArtifacts?.templateDiagnostics) {
        const diag = parseTemplateDiagnostics(dataArtifacts.templateDiagnostics);
        if (diag?.skippedSchemas?.length) {
          console.warn('[Plan] Query template generation skipped schemas:', diag.skippedSchemas);
        }
        if (diag?.failedTemplates?.length) {
          console.warn('[Plan] Query template creation failures:', diag.failedTemplates);
        }
        if (isDebug && diag) {
          debug.getState().pushPhaseSubAgentEvent(phaseId, {
            type: 'diagnostic', data: { templateDiagnostics: diag }, timestamp: Date.now(),
          });
        }
      }

      // NOTE: Do NOT synthesize queryIds/bindingIds from schemaIds — they are different
      // collections. If DataDomainAgent didn't create query templates or bindings, the
      // artifact list should honestly reflect that (empty). Downstream phase prompts
      // use schemaContext and groupIds for data binding, not queryIds/bindingIds.

      // Store full schema context for cross-phase enrichment (field names, types, groupId)
      if (schemas.length > 0) {
        artifacts.schemaContext = schemas.map((s) => {
          const constraints = asRecord(s.constraints);
          const rawFields = Array.isArray(s.fields) ? s.fields
            : Array.isArray(constraints?.fields) ? constraints.fields
            : [];
          const fields = asRecordArray(rawFields)
            .map((f) => `${f.name}:${f.type}`)
            .join(', ');
          const orgId = typeof s.organizationId === 'string' && s.organizationId.trim()
            ? s.organizationId.trim()
            : '';
          return `${s.name || s.displayName}(id:${s.schemaId || s.id || s._id},group:${s.groupId}${orgId ? `,organizationId:${orgId}` : ''},fields:[${fields}])`;
        });
        if (typeof dataArtifacts?.groupId === 'string' && dataArtifacts.groupId) {
          artifacts.groupIds = [dataArtifacts.groupId];
        } else if (typeof schemas[0]?.groupId === 'string' && schemas[0].groupId) {
          artifacts.groupIds = [schemas[0].groupId];
        }
      }

      // Deterministic validation for liaison phases
      if (domain === 'data') {
        const issues: string[] = [];
        const validSchemaIds = (artifacts.schemaIds || []).filter((id: unknown) => typeof id === 'string' && id.length > 0);
        if (!validSchemaIds.length) {
          issues.push('Data phase produced 0 schemas — expected at least 1');
        }
        // Non-blocking warnings when prompt expects artifacts that weren't created
        const promptText = cleanedPrompt || rawPrompt || '';
        if (/##\s*Query Templates/i.test(promptText) && !(artifacts.queryIds?.length)) {
          issues.push('Prompt defines query templates but 0 were created');
        }
        // Note: binding check intentionally omitted here — bindingIds is populated by
        // auto data-binding which runs AFTER executePhase() returns. The synthetic audit
        // at the end of executeAndAuditPhase() validates binding count correctly.
        // passed = schemas exist (blocking). Query/binding issues are warnings tracked in the audit score.
        liaisonValidation = { passed: validSchemaIds.length > 0, issues };

        // Enhancement A: Schema-Query-Binding Alignment Validator
        if (isDebug) {
          const alignment = validateSchemaQueryAlignment(cleanedPrompt || rawPrompt || '', artifacts);
          if (alignment) {
            debug.getState().setSchemaQueryAlignment(phaseId, alignment);
            if (!alignment.aligned) {
              console.warn('[Plan] Schema-query alignment issues:', alignment.issues);
            }
          }
        }
      } else if (domain === 'workflow') {
        liaisonValidation = { passed: true, issues: [] };
      }

      if (isDebug) {
        debug.getState().pushPhaseSubAgentEvent(phaseId, {
          type: 'complete', data: { domain, artifacts, liaisonValidation }, timestamp: Date.now(),
        });
      }
    } else {
      // Visual-editor/interaction: SSE streaming pipeline
      const isInteractionPhase = domain === 'interaction';

      // Detect if a downstream data phase exists — suppress premature "missing dataSource" warnings
      const currentSections = usePlanStore.getState().plan?.sections || [];
      const hasDownstreamDataPhase = domain === 'visual-editor' && currentSections.some(p => p.domain === 'data');

      const classificationMetadata: {
        isFollowUp: boolean;
        complexity: string;
        mentionedBreakpoints: string[];
        domain?: string;
      } = {
        isFollowUp: isInteractionPhase,
        complexity: isInteractionPhase
          ? 'simple-component'
          : 'page-generation',
        mentionedBreakpoints: [],
        domain: isInteractionPhase ? 'interaction' : undefined,
      };

      // Page management for visual-editor phases
      if (pageName && domain === 'visual-editor') {
        const store = useEditorStore.getState();
        // Check if this is the first visual-editor phase — use existing page instead of creating.
        // Use getState() for fresh data; prefer plan.sections (quick mode) over phases (full mode).
        const latestPlan = usePlanStore.getState().plan;
        const allSections = latestPlan?.sections || [];
        const visualPhases = allSections.filter(p => p.domain === 'visual-editor');
        const isFirstVisualPhase = visualPhases.length === 0 || visualPhases[0]?.id === phaseId;

        const isHomeLike = /^(home|dashboard|main|landing|overview)/i.test(pageName || '');
        const isMultiPagePlan = visualPhases.length > 1;

        if (isFirstVisualPhase && (isHomeLike || isMultiPagePlan)) {
          // Home-like page or first of a multi-page plan — use existing canvas page
          const homePage = store.pages.find(p => p.isDefault) || store.pages[0];
          if (homePage) {
            store.updatePage(homePage.id, { name: pageName });
            if (store.currentPageId !== homePage.id) store.switchToPage(homePage.id);
          }
        } else if (!isFirstVisualPhase || !isHomeLike) {
          // Subsequent pages: create a new page
          const newPage = store.addPage({
            name: pageName,
            description: phase.title || pageName,
            template: PageTemplate.FORM,
            order: store.pages.length,
          });
          store.switchToPage(newPage.id);
          collectedPageIds.push(newPage.id);
        }
      } else if (domain === 'visual-editor') {
        // No pageName → this is the main/home page. Ensure we're on it
        const store = useEditorStore.getState();
        const homePage = store.pages.find(p => p.isDefault) || store.pages[0];
        if (homePage && store.currentPageId !== homePage.id) {
          store.switchToPage(homePage.id);
        }
      }

      // Re-serialize after page creation/switch so editorCtx reflects the
      // correct page. The original editorCtx (line 522) has a stale pageId.
      const currentEditorCtx = EditorContextSerializer.serialize();

      await routeToVisualEditorStreaming(
        enrichedPrompt,
        currentEditorCtx,
        {
          onThread: (tid) => {
            if (isDebug) debug.getState().pushPhaseSubAgentEvent(phaseId, { type: 'thread', data: { threadId: tid }, timestamp: Date.now() });
          },
          onStepStart: (data) => {
            planStore.recordSubStepStart(phaseId, data.step);
            if (isDebug) {
              stepStartTimes[data.step] = Date.now();
              debug.getState().pushPhaseSubAgentEvent(phaseId, { type: 'step_start', data, timestamp: Date.now() });
            }
          },
          onStepComplete: (data) => {
            planStore.recordSubStepEnd(phaseId, data.step);
            if (isDebug) {
              // Record step FIRST so tokenUsage extraction in pushPhaseSubAgentEvent can find it
              debug.getState().recordPhaseSubAgentStep(phaseId, {
                agentName: data.agentName ?? data.step,
                step: data.step,
                startedAt: stepStartTimes[data.step] || Date.now(),
                duration: data.duration_ms ?? null,
                actionsCount: data.actionsCount ?? 0,
              });
              debug.getState().pushPhaseSubAgentEvent(phaseId, { type: 'step_complete', data, timestamp: Date.now() });
            }
          },
          onActions: (data) => {
            if (data.replaces) {
              // Final normalized actions — replace entire collection
              collectedActions.length = 0;
              collectedComponentIds.length = 0;
              collectedComponentInfo.length = 0;
              collectedPageIds.length = 0;
              for (const action of (data.actions || [])) {
                collectedActions.push(action);
                if (action.type === 'add_component') {
                  // Handle both nested (payload.component.id) and flat (payload.id) formats
                  const comp = asRecord(action.payload?.component) ?? asRecord(action.payload);
                  if (comp && typeof comp.id === 'string') {
                    const compId = comp.id;
                    collectedComponentIds.push(compId);
                    collectedComponentInfo.push({
                      id: compId,
                      type: typeof comp.type === 'string' ? comp.type : 'unknown',
                      name: typeof comp.name === 'string' ? comp.name : '',
                    });
                  }
                }
                // update_component: reflect type changes in componentInfo (same as incremental path)
                if (action.type === 'update_component') {
                  const changes = asRecord(action.payload?.changes) ?? asRecord(action.payload);
                  const rawTargetId = action.payload?.componentId || action.payload?.id;
                  const targetId = typeof rawTargetId === 'string' ? rawTargetId : undefined;
                  const newType = typeof changes?.type === 'string' ? changes.type : undefined;
                  if (targetId && newType) {
                    const existing = collectedComponentInfo.find(c => c.id === targetId);
                    if (existing) {
                      existing.type = newType;
                    } else {
                      collectedComponentIds.push(targetId);
                      collectedComponentInfo.push({ id: targetId, type: newType, name: typeof changes?.name === 'string' ? changes.name : '' });
                    }
                  }
                }
                if (action.type === 'add_page' && action.payload?.pageId) {
                  const pageId = action.payload.pageId;
                  if (typeof pageId === 'string') collectedPageIds.push(pageId);
                }
              }
              if (isDebug) {
                debug.getState().pushPhaseSubAgentEvent(phaseId, { type: 'actions', data, timestamp: Date.now() });
                const summaries = (data.actions || []).map((a: unknown, i: number) =>
                  summarizeAction(asRecord(a) ?? {}, i));
                debug.getState().replaceActionSummaries(phaseId, summaries);
              }
              return;
            }
            for (const action of (data.actions || [])) {
              collectedActions.push(action);
              // add_component: handle both nested (payload.component.id) and flat (payload.id) formats
              if (action.type === 'add_component') {
                const comp = asRecord(action.payload?.component) ?? asRecord(action.payload);
                if (comp && typeof comp.id === 'string') {
                  const compId = comp.id;
                  collectedComponentIds.push(compId);
                  collectedComponentInfo.push({
                    id: compId,
                    type: typeof comp.type === 'string' ? comp.type : 'unknown',
                    name: typeof comp.name === 'string' ? comp.name : '',
                  });
                }
              }
              // update_component may change the type (e.g. text → data-text from the Data Component Agent).
              // Reflect these type changes in collectedComponentInfo so downstream validation
              // (e.g. interaction KPI detection) sees the final component types, not just initial ones.
              if (action.type === 'update_component') {
                const changes = asRecord(action.payload?.changes) ?? asRecord(action.payload);
                const rawTargetId = action.payload?.componentId || action.payload?.id;
                const targetId = typeof rawTargetId === 'string' ? rawTargetId : undefined;
                const newType = typeof changes?.type === 'string' ? changes.type : undefined;
                if (targetId && newType) {
                  const existing = collectedComponentInfo.find(c => c.id === targetId);
                  if (existing) {
                    existing.type = newType;
                  } else {
                    // Data Component Agent may add_component for hidden helpers (e.g. first-weight-holder)
                    collectedComponentIds.push(targetId);
                    collectedComponentInfo.push({
                      id: targetId,
                      type: newType,
                      name: typeof changes?.name === 'string' ? changes.name : '',
                    });
                  }
                }
              }
              if (action.type === 'add_page' && action.payload?.pageId) {
                const pageId = action.payload.pageId;
                if (typeof pageId === 'string') collectedPageIds.push(pageId);
              }
            }
            if (isDebug) {
              debug.getState().pushPhaseSubAgentEvent(phaseId, { type: 'actions', data, timestamp: Date.now() });
              // Enhancement B: Build action payload summaries
              const baseIndex = collectedActions.length - (data.actions?.length || 0);
              const summaries = (data.actions || []).map((a: unknown, i: number) => summarizeAction(asRecord(a) ?? {}, baseIndex + i));
              debug.getState().appendActionSummaries(phaseId, summaries);
            }
          },
          onComplete: (data) => {
            if (isDebug) debug.getState().pushPhaseSubAgentEvent(phaseId, { type: 'complete', data, timestamp: Date.now() });
          },
          onError: (data) => {
            hadStreamingErrors = true;
            console.error('[Plan] Streaming error:', data);
            if (isDebug) debug.getState().pushPhaseSubAgentEvent(phaseId, { type: 'error', data, timestamp: Date.now() });
          },
          onMessage: (data) => {
            if (isDebug) debug.getState().pushPhaseSubAgentEvent(phaseId, { type: 'message', data, timestamp: Date.now() });
          },
          onDiagnostic: (data) => {
            if (isDebug) debug.getState().pushPhaseSubAgentEvent(phaseId, { type: 'diagnostic', data, timestamp: Date.now() });
          },
          onVerificationResult: (data) => {
            if (isDebug) debug.getState().pushPhaseSubAgentEvent(phaseId, { type: 'verification_result', data, timestamp: Date.now() });
            // Keep only the latest verification result per step so stale retry
            // failures do not survive a later successful verification.
            const nextIssues = replaceStepVerificationIssues({
              existingIssues: collectedVerificationIssues,
              step: data.step,
              latestIssues: data.issues,
            });
            collectedVerificationIssues.splice(0, collectedVerificationIssues.length, ...nextIssues);
          },
          onSubAgentRetry: (data) => {
            if (isDebug) {
              debug.getState().pushPhaseSubAgentEvent(phaseId, { type: 'sub_agent_retry', data, timestamp: Date.now() });
              debug.getState().recordPhaseRetry(phaseId, {
                step: data.step,
                agent: data.agent,
                attemptNumber: data.attemptNumber,
                retryType: data.retryType,
                reason: data.reason,
                potentialFalsePositive: data.potentialFalsePositive,
                previousAttempt: data.previousAttempt,
                gateValues: data.gateValues,
              });
            }
          },
          onSubAgentRetryResult: (data) => {
            if (isDebug) {
              debug.getState().pushPhaseSubAgentEvent(phaseId, { type: 'sub_agent_retry_result', data, timestamp: Date.now() });
              debug.getState().recordPhaseRetryResult(phaseId, {
                step: data.step,
                attemptNumber: data.attemptNumber,
                retryType: data.retryType,
                durationMs: data.durationMs,
                actionCount: data.actionCount,
                improved: data.improved,
                acceptedAsResult: data.acceptedAsResult,
                metrics: data.metrics,
              });
            }
          },
          onSubAgentAttemptsSummary: (data) => {
            if (isDebug) {
              debug.getState().pushPhaseSubAgentEvent(phaseId, { type: 'sub_agent_attempts_summary', data, timestamp: Date.now() });
              debug.getState().recordPhaseAttemptsSummary(phaseId, data);
            }
          },
          onPipelineTiming: (data) => {
            if (isDebug) {
              debug.getState().pushPhaseSubAgentEvent(phaseId, { type: 'pipeline_timing', data, timestamp: Date.now() });
              debug.getState().recordPhasePipelineTiming(phaseId, data);
            }
          },
        },
        undefined, // New execution thread per phase
        undefined, // sessionContext
        classificationMetadata,
        hasDownstreamDataPhase ? { suppressDataBindingCheck: true } : undefined,
      );
      planStore.incrementLLMCalls();
    }

    const canonicalizedVisualArtifacts = canonicalizePublishedVisualComponentArtifacts({
      componentIds: collectedComponentIds,
      componentInfo: collectedComponentInfo,
    });

    if (canonicalizedVisualArtifacts.componentIds.length) {
      artifacts.componentIds = canonicalizedVisualArtifacts.componentIds;
    }
    if (collectedPageIds.length) artifacts.pageIds = collectedPageIds;
    if (collectedActions.length) artifacts._actions = collectedActions.map(a => JSON.stringify(a).slice(0, 2000));

    // Build typed component summary for interaction phase enrichment
    if (canonicalizedVisualArtifacts.componentInfo.length > 0) {
      const byType: Record<string, string[]> = {};
      const seenSummaryIds = new Set<string>();
      for (const info of canonicalizedVisualArtifacts.componentInfo) {
        if (seenSummaryIds.has(info.id)) continue;
        seenSummaryIds.add(info.id);
        if (!byType[info.type]) byType[info.type] = [];
        byType[info.type].push(info.name ? `${info.id} ("${info.name}")` : info.id);
      }
      artifacts.componentSummary = Object.entries(byType)
        .map(([type, ids]) => `${type}: [${ids.join(', ')}]`);
    }

    // Build page context for interaction phase enrichment (pageName→pageId mapping)
    if (collectedPageIds.length && pageName) {
      if (!artifacts.pageContext) artifacts.pageContext = [];
      artifacts.pageContext.push(`${pageName}(pageId:${collectedPageIds[collectedPageIds.length - 1]})`);
    }

    // Extract schema/group IDs from component data sources in streaming actions
    const schemaIdsFromActions = new Set<string>();
    const groupIdsFromActions = new Set<string>();
    for (const action of collectedActions) {
      const aRec = asRecord(action);
      const payloadRec = asRecord(aRec?.payload);
      const compRec = asRecord(payloadRec?.component);
      const props = asRecord(compRec?.properties);
      const ds = asRecord(props?.dataSource);
      if (ds?.schemaId && typeof ds.schemaId === 'string' && /^[a-f0-9]{24}$/.test(ds.schemaId)) {
        schemaIdsFromActions.add(ds.schemaId);
      }
      if (ds?.groupId && typeof ds.groupId === 'string' && /^[a-f0-9]{24}$/.test(ds.groupId)) {
        groupIdsFromActions.add(ds.groupId);
      }
      // Also check direct button properties (submit-form-data)
      if (props?.schemaId && typeof props.schemaId === 'string' && /^[a-f0-9]{24}$/.test(props.schemaId)) {
        schemaIdsFromActions.add(props.schemaId);
      }
      if (props?.groupId && typeof props.groupId === 'string' && /^[a-f0-9]{24}$/.test(props.groupId)) {
        groupIdsFromActions.add(props.groupId);
      }
    }
    if (schemaIdsFromActions.size) artifacts.schemaIds = [...(artifacts.schemaIds || []), ...schemaIdsFromActions];
    if (groupIdsFromActions.size) artifacts.groupIds = [...(artifacts.groupIds || []), ...groupIdsFromActions];

    // Accumulate artifacts keyed by domain (skip _actions metadata).
    // Interaction phases modify visual-editor components, so accumulate under visual-editor.
    const accumulationDomain = domain === 'interaction' ? 'visual-editor' : domain;
    if (!accumulatedArtifacts.current[accumulationDomain]) {
      accumulatedArtifacts.current[accumulationDomain] = {};
    }
    for (const [key, val] of Object.entries(artifacts)) {
      if (key === '_actions') continue;
      if (!accumulatedArtifacts.current[accumulationDomain][key]) {
        accumulatedArtifacts.current[accumulationDomain][key] = [];
      }
      const existing = new Set(accumulatedArtifacts.current[accumulationDomain][key]);
      for (const id of val) {
        if (!existing.has(id)) {
          accumulatedArtifacts.current[accumulationDomain][key].push(id);
          existing.add(id);
        }
      }
    }

    return { artifacts, hadStreamingErrors, liaisonValidation, collectedVerificationIssues, unresolvedTokens, enrichedPrompt };
  }, [
    abortControllerRef,
    accumulatedArtifacts,
    accumulatedFeedback,
    debug,
    debugEvent,
    planQualityGate,
    planStore,
  ]);

  // ── executeAllPhases (Full Mode) ───────────────────────────────────────────

  const executeAllPhases = useCallback(async () => {
    if (!planStore.roadmap) {
      planStore.setError('No roadmap available');
      advanceStage('failed');
      return;
    }
    const roadmap = planStore.roadmap;
    const sorted = topologicalSort(planStore.phases);
    planStore.setExecuting(true);

    // Initialize plan debug for saved-roadmap execution path (bypasses handlePlanMessage)
    if (debug.getState().isEnabled && !debug.getState().planDebugData) {
      debug.getState().initPlanDebug();
    }

    // Record pipeline start if not already set (saved-roadmap execution path)
    if (!planStore.pipelineStartedAt) {
      planStore.setPipelineStartedAt(Date.now());
    }
    advanceStage('executing_phase');
    executionStartTimeRef.current = Date.now();
    accumulatedArtifacts.current = {};
    accumulatedFeedback.current = [];
    isPausedRef.current = false;

    const allPhaseResults: Array<{ phaseId: string; actions: unknown[]; artifacts: Record<string, string[]> }> = [];
    const isDebug = debug.getState().isEnabled;

    // Helper: execute a single phase with optional audit + retry
    const executeAndAuditPhase = async (phase: typeof sorted[number], phaseIndex: number) => {
      planStore.setCurrentExecutingPhaseId(phase.id);
      planStore.setPipelineProgress(Math.round((phaseIndex / sorted.length) * 100));

      const phaseExecStart = Date.now();
      debugEvent('step_start', { step: 'plan_phase_execute', phaseId: phase.id, phaseNumber: phaseIndex + 1, total: sorted.length });

      try {
        let { artifacts, hadStreamingErrors, liaisonValidation, collectedVerificationIssues, unresolvedTokens, enrichedPrompt: currentEnrichedPrompt } = await executePhase(phase.id);
        let retried = false;
        let bindingActionCount = 0;

        // Parse collected actions for audit.
        // Use the enriched prompt (with {{section:...}} tokens resolved) so the auditor
        // sees the same ground truth the agent executed against. Phase 3 interaction prompts
        // contain unresolved tokens like {{section:visual-editor:componentIds}} until
        // enrichExecutionPrompt() inlines actual IDs — passing the raw prompt causes the
        // auditor to hallucinate "missing references" (false-positive WARN).
        const phasePromptObj = {
          executionPrompt: currentEnrichedPrompt,
          domain: phase.domain,
        };
        const isLiaisonPhase = phase.domain === 'data' || phase.domain === 'workflow';
        let auditActions: unknown[];
        if (artifacts._actions?.length) {
          auditActions = artifacts._actions.map(parseAuditAction);
        } else if (isLiaisonPhase) {
          // Liaison phases produce artifacts (schemas, queries, bindings), not canvas actions.
          // Wrap them as a typed summary so summarizePhaseResults() can format them meaningfully
          // instead of flattening raw ID strings into "unknown" actions.
          const { _actions, ...artifactsWithoutActions } = artifacts;
          auditActions = [{
            type: 'data_phase_summary',
            domain: phase.domain,
            artifacts: artifactsWithoutActions,
          }];
        } else {
          auditActions = Object.values(artifacts).flat();
        }

        // Flatten all domain artifacts into a single Record<string, string[]> for the auditor (deduplicated)
        const flatArtifacts: Record<string, string[]> = {};
        for (const domainArtifacts of Object.values(accumulatedArtifacts.current)) {
          for (const [key, val] of Object.entries(domainArtifacts)) {
            if (!flatArtifacts[key]) flatArtifacts[key] = [];
            const existing = new Set(flatArtifacts[key]);
            for (const id of val) {
              if (!existing.has(id)) {
                flatArtifacts[key].push(id);
                existing.add(id);
              }
            }
          }
        }


        let implAudit: { data: { auditResult: AuditResult } | null } | null = null;
        if (!isLiaisonPhase) {
          // Enhancement E: Compute grounding context from data phase alignment for audit
          let groundingContext: Record<string, unknown> | undefined;
          const dataArtifactsForGrounding = accumulatedArtifacts.current['data'];
          if (dataArtifactsForGrounding?.schemaContext?.length) {
            const alignment = validateSchemaQueryAlignment(
              phase.executionPrompt || '',
              dataArtifactsForGrounding,
            );
            if (alignment && !alignment.aligned) {
              groundingContext = { schemaQueryAlignment: alignment };
            }
          }

          // Enhancement J: Feed error-severity verification issues into audit grounding
          const errorVerificationIssues = collectedVerificationIssues.filter((i: { severity: string }) => i.severity === 'error');
          if (errorVerificationIssues.length > 0) {
            if (!groundingContext) groundingContext = {};
            groundingContext.verificationIssues = errorVerificationIssues;
          }

          // Run implementation audit — semantic validation with proper scoring rubrics
          implAudit = await planApi.auditImplementation(
            phase.id,
            auditActions,
            phasePromptObj,
            flatArtifacts,
            planStore.currentThreadId ?? undefined,
            abortControllerRef.current?.signal,
            isDebug,
            groundingContext,
          );
          planStore.incrementLLMCalls();
          captureDebugPrompts(implAudit, 'impl_audit', phase.id);
        } else {
          console.info(`[Plan] Skipping implementation audit for liaison phase: ${phase.id} (domain: ${phase.domain})`);
        }

        // Corrective retry loop: only retry when failures are likely recoverable.
        let retryCount = 0;
        const MAX_PHASE_RETRIES = 2;
        const CATASTROPHIC_THRESHOLD = 30;
        let retryDecision = decidePhaseRetry({
          domain: phase.domain,
          hadStreamingErrors,
          liaisonValidation,
          implAudit,
          catastrophicThreshold: CATASTROPHIC_THRESHOLD,
        });

        if (!retryDecision.shouldRetry && retryDecision.reason !== 'phase passed') {
          debugEvent('quality_gate', {
            stage: 'plan_phase_execute',
            phaseId: phase.id,
            verdict: 'fail',
            action: 'stop_retry',
            reason: retryDecision.reason,
            strategy: retryDecision.strategy,
          });
          planQualityGate({
            stage: 'executing_phase',
            phaseId: phase.id,
            verdict: 'fail',
            action: 'stop_retry',
          });
        }

        while (retryDecision.shouldRetry && retryCount < MAX_PHASE_RETRIES) {
          retryCount++;
          retried = true;

          if (isLiaisonPhase) {
            // Liaison retry: re-execute with same prompt, then re-validate
            debugEvent('quality_gate', {
              stage: 'plan_phase_execute', phaseId: phase.id,
              verdict: 'fail', action: 'retry', retryNumber: retryCount, strategy: retryDecision.strategy, auditScore: 0, reason: retryDecision.reason,
            });
            planQualityGate({
              stage: 'executing_phase', phaseId: phase.id,
              verdict: 'fail', action: 'retry', retryNumber: retryCount,
            });

            const retryResult = await executePhase(phase.id);
            artifacts = retryResult.artifacts;
            hadStreamingErrors = retryResult.hadStreamingErrors;
            liaisonValidation = retryResult.liaisonValidation;
            collectedVerificationIssues = retryResult.collectedVerificationIssues;
            unresolvedTokens = retryResult.unresolvedTokens;
            currentEnrichedPrompt = retryResult.enrichedPrompt;

            if (artifacts._actions?.length) {
              auditActions = artifacts._actions.map(parseAuditAction);
            } else {
              const { _actions, ...artifactsWithoutActions } = artifacts;
              auditActions = [{
                type: 'data_phase_summary',
                domain: phase.domain,
                artifacts: artifactsWithoutActions,
              }];
            }

            retryDecision = decidePhaseRetry({
              domain: phase.domain,
              hadStreamingErrors,
              liaisonValidation,
              implAudit,
              catastrophicThreshold: CATASTROPHIC_THRESHOLD,
            });
            continue;
          }

          const auditScore = implAudit?.data?.auditResult?.overallScore ?? 0;
          const strategy = retryDecision.strategy;

          debugEvent('quality_gate', {
            stage: 'plan_phase_execute', phaseId: phase.id,
            verdict: 'fail', action: 'retry', retryNumber: retryCount, strategy, auditScore, reason: retryDecision.reason,
          });
          planQualityGate({
            stage: 'executing_phase', phaseId: phase.id,
            verdict: 'fail', action: 'retry', retryNumber: retryCount,
          });

          let retryPrompt: string;

          if (implAudit == null || implAudit.data == null) {
            retryPrompt = phase.executionPrompt;
          } else if (strategy === 'regenerate') {
            // Catastrophic failure: LLM regenerates the entire prompt using audit feedback
            const regenResult = await planApi.regeneratePhase(
              phase.id,
              implAudit.data.auditResult,
              roadmap,
              abortControllerRef.current?.signal,
              isDebug,
            );
            planStore.incrementLLMCalls();
            captureDebugPrompts(regenResult, 'phase_regenerate', phase.id);
            retryPrompt = regenResult.data?.phase?.executionPrompt || phase.executionPrompt;
          } else {
            // Partial failure: append specific audit feedback to existing prompt
            const feedback = implAudit.data.auditResult.criteria
              ?.filter((c: AuditCriterion) => c.verdict !== 'pass')
              .map((c: AuditCriterion) => `- [${c.name}]: ${c.reasoning}`)
              .join('\n') || '';
            retryPrompt = `${phase.executionPrompt}\n\n[AUDIT FEEDBACK — fix these issues]\n${feedback}`;
          }

          // Re-execute with chosen prompt
          const retryResult = await executePhase(phase.id, retryPrompt);
          artifacts = retryResult.artifacts;
          hadStreamingErrors = retryResult.hadStreamingErrors;
          liaisonValidation = retryResult.liaisonValidation;
          collectedVerificationIssues = retryResult.collectedVerificationIssues;
          unresolvedTokens = retryResult.unresolvedTokens;
          currentEnrichedPrompt = retryResult.enrichedPrompt;

          // Rebuild audit actions
          if (artifacts._actions?.length) {
            auditActions = artifacts._actions.map(parseAuditAction);
          } else {
            auditActions = Object.values(artifacts).flat();
          }

          // Re-audit with the post-enrichment prompt (retryPrompt is pre-enrichment;
          // executePhase re-runs enrichExecutionPrompt internally and returns the resolved version)
          implAudit = await planApi.auditImplementation(
            phase.id, auditActions, { executionPrompt: currentEnrichedPrompt, domain: phase.domain },
            flatArtifacts,
            planStore.currentThreadId ?? undefined,
            abortControllerRef.current?.signal,
            isDebug,
          );
          planStore.incrementLLMCalls();
          captureDebugPrompts(implAudit, 'impl_audit', phase.id);

          retryDecision = decidePhaseRetry({
            domain: phase.domain,
            hadStreamingErrors,
            liaisonValidation,
            implAudit,
            catastrophicThreshold: CATASTROPHIC_THRESHOLD,
          });
          if (!retryDecision.shouldRetry && retryDecision.reason !== 'phase passed') {
            debugEvent('quality_gate', {
              stage: 'plan_phase_execute',
              phaseId: phase.id,
              verdict: 'fail',
              action: 'stop_retry',
              reason: retryDecision.reason,
              strategy: retryDecision.strategy,
            });
            planQualityGate({
              stage: 'executing_phase',
              phaseId: phase.id,
              verdict: 'fail',
              action: 'stop_retry',
            });
          }
        }

        const persistentInteractionVerificationFailure = getPersistentInteractionVerificationFailure({
          phase,
          verificationIssues: collectedVerificationIssues,
        });
        if (persistentInteractionVerificationFailure) {
          const failureOutcome = buildPersistentInteractionPhaseFailureOutcome({
            phase,
            failure: persistentInteractionVerificationFailure,
          });
          implAudit = {
            data: {
              auditResult: failureOutcome.implementationAudit,
            },
          };
          planStore.setExecutionError(phase.id, failureOutcome.executionError);
          debugEvent('quality_gate', {
            stage: 'plan_phase_execute',
            phaseId: phase.id,
            verdict: 'fail',
            action: 'block_execution',
            reason: failureOutcome.executionError,
          });
          planQualityGate({
            stage: 'executing_phase',
            phaseId: phase.id,
            verdict: 'fail',
            action: 'block_execution',
          });
        }

        if (implAudit?.data?.auditResult) {
          implAudit.data.auditResult = applyDeterministicExecutionDegradation({
            auditResult: implAudit.data.auditResult,
            unresolvedTokens,
            verificationIssues: collectedVerificationIssues,
          });
        }

        // Opt 7: Accumulate audit feedback for subsequent phases
        if (implAudit) {
          const auditFeedback = implAudit.data?.auditResult?.criteria
            ?.filter((c: AuditCriterion) => c.verdict !== 'pass')
            .map((c: AuditCriterion) => `[${c.name}]: ${c.reasoning}`)
            .join('\n');
          if (auditFeedback) {
            accumulatedFeedback.current.push(auditFeedback);
          }
          if (implAudit.data?.auditResult) {
            planStore.setImplementationAudit(phase.id, implAudit.data.auditResult);
          }
        } else {
          // ── Auto data-binding: run BEFORE synthetic audit so bindingIds are ──
          // ── populated when the audit scores the data_bindings criterion.    ──
          if (phase.domain === 'data' && (artifacts.schemaIds?.length ?? 0) > 0) {
            const hasComponents = !!(accumulatedArtifacts.current['visual-editor']?.componentIds?.length);

            if (!hasComponents) {
              console.warn(
                '[Plan] Auto data-binding SKIPPED: data phase produced schemas but no visual-editor components exist yet. ' +
                'This typically means the data phase ran before the visual-editor phase. ' +
                'Bindings will need to be created manually or in a follow-up phase.'
              );
              debugEvent('quality_gate', {
                stage: 'auto_data_binding', phaseId: phase.id,
                verdict: 'warn', action: 'skipped',
                reason: 'no_components_yet',
              });
            }

            if (hasComponents && !abortControllerRef.current?.signal.aborted) {
              console.info('[Plan] Auto data-binding: data phase produced schemas, binding to existing components');
              debugEvent('step_start', { step: 'auto_data_binding', phaseId: phase.id });

              const collectedBindingActions: unknown[] = [];

              try {
                // Build binding prompt with schema/query context from accumulated artifacts
                const schemaContext = accumulatedArtifacts.current['data']?.schemaContext?.join('\n') || '';
                const queryTemplateContext = accumulatedArtifacts.current['data']?.queryTemplateContext?.join('\n') || '';
                const groupIds = accumulatedArtifacts.current['data']?.groupIds || [];

                const bindingPrompt = buildPhase11BAutoDataBindingPrompt({
                  schemaContext,
                  queryTemplateContext,
                  hasSchemaGroups: groupIds.length > 0,
                });

                // Re-serialize editor context to capture current canvas (after visual-editor phases)
                const bindingEditorCtx = EditorContextSerializer.serialize();

                const bindingClassification: {
                  isFollowUp: boolean;
                  complexity: string;
                  mentionedBreakpoints: string[];
                  domain?: string;
                } = {
                  isFollowUp: false,
                  complexity: 'simple-component',
                  mentionedBreakpoints: [],
                  domain: 'data-binding',
                };

                await routeToVisualEditorStreaming(
                  bindingPrompt,
                  bindingEditorCtx,
                  {
                    onThread: (tid) => {
                      if (isDebug) debug.getState().pushPhaseSubAgentEvent(phase.id, { type: 'thread', data: { threadId: tid }, timestamp: Date.now() });
                    },
                    onStepStart: (data) => {
                      planStore.recordSubStepStart(phase.id, `data-binding:${data.step}`);
                      if (isDebug) debug.getState().pushPhaseSubAgentEvent(phase.id, { type: 'step_start', data: { ...data, step: `data-binding:${data.step}` }, timestamp: Date.now() });
                    },
                    onStepComplete: (data) => {
                      planStore.recordSubStepEnd(phase.id, `data-binding:${data.step}`);
                      if (isDebug) debug.getState().pushPhaseSubAgentEvent(phase.id, { type: 'step_complete', data, timestamp: Date.now() });
                    },
                    onActions: (data) => {
                      if (data.actions) collectedBindingActions.push(...data.actions);
                      if (isDebug) debug.getState().pushPhaseSubAgentEvent(phase.id, { type: 'actions', data, timestamp: Date.now() });
                    },
                    onComplete: (data) => {
                      if (isDebug) debug.getState().pushPhaseSubAgentEvent(phase.id, { type: 'complete', data, timestamp: Date.now() });
                    },
                    onError: (data) => {
                      console.warn('[Plan] Auto data-binding error:', data);
                      if (isDebug) debug.getState().pushPhaseSubAgentEvent(phase.id, { type: 'error', data, timestamp: Date.now() });
                    },
                    onMessage: (data) => {
                      if (isDebug) debug.getState().pushPhaseSubAgentEvent(phase.id, { type: 'message', data, timestamp: Date.now() });
                    },
                    onDiagnostic: (data) => {
                      if (isDebug) debug.getState().pushPhaseSubAgentEvent(phase.id, { type: 'diagnostic', data, timestamp: Date.now() });
                    },
                    onVerificationResult: (data) => {
                      if (isDebug) debug.getState().pushPhaseSubAgentEvent(phase.id, { type: 'verification_result', data, timestamp: Date.now() });
                    },
                  },
                  undefined, // New thread
                  undefined, // No session context
                  bindingClassification,
                );
                planStore.incrementLLMCalls();

                // Track binding actions in phase results so the final auditor sees them
                if (collectedBindingActions.length > 0) {
                  auditActions.push(...collectedBindingActions);
                  bindingActionCount = collectedBindingActions.length;
                  // Populate bindingIds from DC Agent actions so the synthetic audit
                  // correctly recognizes that data-to-component wiring was performed
                  artifacts.bindingIds = collectedBindingActions
                    .map((a: unknown) => {
                      const rec = asRecord(a);
                      const p = asRecord(rec?.payload);
                      // Handle all normalizer output formats:
                      // - payload.componentId (standard update_component)
                      // - payload.component.id (pre-normalization add_component/update_component)
                      // - payload.changes.componentId (LLM nests componentId inside changes)
                      // - payload.changes.id (post-normalization: component renamed to changes)
                      // - payload.id (flat format from some LLM outputs)
                      const comp = asRecord(p?.component);
                      const changes = asRecord(p?.changes);
                      const id = p?.componentId || comp?.id || changes?.componentId || changes?.id || p?.id;
                      return typeof id === 'string' ? id : undefined;
                    })
                    .filter((id): id is string => id !== undefined);
                  console.info(`[Plan] Auto data-binding: ${collectedBindingActions.length} binding actions applied to components`);

                  // Refresh the data_phase_summary snapshot so summarizePhaseResults()
                  // sees the updated bindingIds (the original snapshot was taken before
                  // auto data-binding ran and has stale/empty bindingIds).
                  const summaryIdx = auditActions.findIndex((a: unknown) => {
                    const rec = asRecord(a);
                    return rec?.type === 'data_phase_summary';
                  });
                  if (summaryIdx >= 0) {
                    const { _actions: _discarded, ...freshArtifacts } = artifacts;
                    const summaryRec = asRecord(auditActions[summaryIdx]);
                    if (summaryRec) summaryRec.artifacts = freshArtifacts;
                  }
                } else {
                  console.warn('[Plan] Auto data-binding: DC Agent returned 0 actions');
                }

                debugEvent('step_complete', { step: 'auto_data_binding', phaseId: phase.id, bindingActions: collectedBindingActions.length });
                console.info('[Plan] Auto data-binding complete');
              } catch (bindingErr) {
                console.warn('[Plan] Auto data-binding failed (non-fatal):', toErrMsg(bindingErr));
                debugEvent('error', { stage: 'auto_data_binding', phaseId: phase.id, error: toErrMsg(bindingErr) });
              }
            }
          }

          // Liaison phases — deterministic scoring with proportional criteria
          const promptText = phase.executionPrompt || '';
          // Match both section headings ("## Query Templates") and task-list
          // mentions ("Create query templates…", "Bind dataSource queries…").
          const expectsQueryTemplates = /query.?templates?/i.test(promptText);
          const expectsBindings = /\bbind(?:ing)?s?\b/i.test(promptText);

          const schemaCount = (artifacts.schemaIds || []).filter((id: string) => typeof id === 'string' && id.length > 0).length;
          const queryCount = (artifacts.queryIds || []).length;

          // Binding count: prefer extracted IDs, fall back to action count, then to scanning
          // auditActions for components with dataSource. This belt-and-suspenders approach
          // catches cases where ID extraction fails OR where bindings happened outside the
          // auto-binding step (e.g., DataDomainAgent set bindingIds directly).
          let bindingCount = (artifacts.bindingIds || []).length;
          if (bindingCount === 0) {
            // Fallback 1: use the raw action count tracked during auto-binding
            if (bindingActionCount > 0) {
              bindingCount = bindingActionCount;
            }
          }
          if (bindingCount === 0 && Array.isArray(auditActions)) {
            // Fallback 2: scan auditActions for components with dataSource bindings.
            // This catches cases where the visual-editor phase already created components
            // with dataSource (e.g., DC Agent ran inline) but bindingIds wasn't populated.
            const boundIds = new Set<string>();
            for (const a of auditActions) {
              const ar = asRecord(a);
              if (!ar) continue;
              const p = asRecord(ar.payload) ?? {};
              const pComp = asRecord(p.component);
              const pChanges = asRecord(p.changes);
              const pProps = asRecord(p.properties);
              const compId = p.componentId || pComp?.id || pChanges?.componentId || pChanges?.id || p.id;
              const changesProps = asRecord(pChanges?.properties);
              const compProps = asRecord(pComp?.properties);
              const ds = pChanges?.dataSource
                || changesProps?.dataSource
                || compProps?.dataSource
                || pComp?.dataSource
                || pProps?.dataSource;
              if (typeof compId === 'string' && ds && typeof ds === 'object') {
                boundIds.add(compId);
              }
            }
            if (boundIds.size > 0) bindingCount = boundIds.size;
          }

          // Build criteria with weighted scoring
          type SynthCriterion = {
            name: string; score: number; verdict: 'pass' | 'warn' | 'fail';
            reasoning: string; feedback: Array<{ text: string; sentiment: 'positive' | 'negative' | 'neutral' }>;
            suggestions: string[]; weight: number;
          };
          const synthCriteria: SynthCriterion[] = [];
          let weightedSum = 0;
          let totalWeight = 0;

          // Schema criterion (always expected, weight 0.50)
          const schemaScore = schemaCount > 0 ? 100 : 0;
          synthCriteria.push({
            name: 'schema_creation', score: schemaScore, weight: 0.50,
            verdict: schemaScore >= 80 ? 'pass' : 'fail',
            reasoning: schemaCount > 0
              ? `Created ${schemaCount} schema(s) successfully`
              : 'No schemas were created — blocking failure',
            feedback: [{ text: schemaCount > 0 ? `${schemaCount} schema(s) created` : 'Zero schemas created', sentiment: schemaCount > 0 ? 'positive' : 'negative' }],
            suggestions: schemaCount === 0 ? ['Re-execute data phase to create schemas'] : [],
          });
          weightedSum += schemaScore * 0.50;
          totalWeight += 0.50;

          // Query template criterion (if prompt expects them, weight 0.30)
          if (expectsQueryTemplates) {
            const queryScore = queryCount > 0 ? 100 : 0;
            synthCriteria.push({
              name: 'query_templates', score: queryScore, weight: 0.30,
              verdict: queryScore >= 80 ? 'pass' : 'fail',
              reasoning: queryCount > 0
                ? `Created ${queryCount} query template(s)`
                : 'Prompt requested query templates but 0 were created',
              feedback: [{ text: queryCount > 0 ? `${queryCount} query template(s) created` : 'Zero query templates — data display components will show empty states', sentiment: queryCount > 0 ? 'positive' : 'negative' }],
              suggestions: queryCount === 0 ? ['DataDomainAgent should generate query templates for data-bound components (charts, tables, KPIs)'] : [],
            });
            weightedSum += queryScore * 0.30;
            totalWeight += 0.30;
          }

          // Binding criterion (if prompt expects them, weight 0.20)
          if (expectsBindings) {
            const bindingScore = bindingCount > 0 ? 100 : 0;
            synthCriteria.push({
              name: 'data_bindings', score: bindingScore, weight: 0.20,
              verdict: bindingScore >= 80 ? 'pass' : 'fail',
              reasoning: bindingCount > 0
                ? `Created ${bindingCount} data binding(s)`
                : 'Prompt requested data bindings but 0 were created',
              feedback: [{ text: bindingCount > 0 ? `${bindingCount} binding(s) created` : 'Zero data bindings — schemas not connected to visual components', sentiment: bindingCount > 0 ? 'positive' : 'negative' }],
              suggestions: bindingCount === 0 ? ['Data bindings connect schemas/queries to visual components'] : [],
            });
            weightedSum += bindingScore * 0.20;
            totalWeight += 0.20;
          }

          // Normalized weighted average
          const overallScore = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 100;
          const verdict = overallScore >= 80 ? ('pass' as const)
            : overallScore >= 50 ? ('warn' as const)
            : ('fail' as const);

          const blockingIssues = synthCriteria
            .filter(c => c.verdict === 'fail' && c.name === 'schema_creation')
            .map(c => c.reasoning);
          const warningMessages = synthCriteria
            .filter(c => c.verdict === 'fail' && c.name !== 'schema_creation')
            .map(c => c.reasoning);

          if (verdict !== 'pass') {
            console.warn(`[Plan] Liaison phase ${phase.id} scored ${overallScore}/100 (${verdict}):`,
              [...blockingIssues, ...warningMessages]);
          }

          // Accumulate failed criteria feedback for subsequent phases
          const synthFeedback = synthCriteria
            .filter(c => c.verdict !== 'pass')
            .map(c => `[${c.name}]: ${c.reasoning}`)
            .join('\n');
          if (synthFeedback) {
            accumulatedFeedback.current.push(synthFeedback);
          }

          planStore.setImplementationAudit(phase.id, {
            id: `synth-${phase.id}`,
            auditType: 'implementation',
            targetId: phase.id,
            verdict,
            overallScore,
            criteria: synthCriteria,
            suggestions: synthCriteria.flatMap(c => c.suggestions),
            blockingIssues,
            warnings: warningMessages,
            auditedAt: new Date().toISOString(),
            modelUsed: 'synthetic',
            retryCount: 0,
          });
        }

        allPhaseResults.push({ phaseId: phase.id, actions: auditActions, artifacts });
        debugEvent('step_complete', { step: 'plan_phase_execute', phaseId: phase.id, duration_ms: Date.now() - phaseExecStart, verdict: implAudit?.data?.auditResult?.verdict ?? 'pass' });

        // Record completion in plan debug data
        if (debug.getState().isEnabled) {
          // Debug 3: Action type breakdown
          const actionBreakdown: Record<string, number> = {};
          for (const a of auditActions) {
            const rec = asRecord(a);
            const t = typeof rec?.type === 'string' ? rec.type : 'unknown';
            actionBreakdown[t] = (actionBreakdown[t] || 0) + 1;
          }
          debug.getState().completePhaseExecution(phase.id, {
            implAuditResult: implAudit?.data?.auditResult ?? { verdict: 'pass', overallScore: 100 },
            retried,
            actionsProduced: auditActions.length,
            componentIds: artifacts.componentIds || [],
            pageIds: artifacts.pageIds || [],
            actionBreakdown,
          });
        }
      } catch (err) {
        debugEvent('error', { stage: 'plan_phase_execute', phaseId: phase.id, error: toErrMsg(err) });
        planStore.setExecutionError(phase.id, toErrMsg(err));
        if (debug.getState().isEnabled) {
          debug.getState().completePhaseExecution(phase.id, { error: toErrMsg(err) });
        }
      }
    };

    // Opt 6: Group phases by dependency depth for parallel execution.
    // Phases at the same depth are independent and can run concurrently.
    const groupByDepth = <T extends { id: string; dependsOn: string[] }>(phases: T[]): T[][] => {
      const completed = new Set<string>();
      const levels: T[][] = [];
      const remaining = [...phases];

      while (remaining.length > 0) {
        const level = remaining.filter(p =>
          (p.dependsOn ?? []).every(dep => completed.has(dep)),
        );
        if (level.length === 0) break; // Safety: avoid infinite loop on bad deps
        levels.push(level);
        for (const p of level) {
          completed.add(p.id);
          const idx = remaining.indexOf(p);
          if (idx >= 0) remaining.splice(idx, 1);
        }
      }
      return levels;
    };

    try {
      const levels = groupByDepth(sorted);
      let phaseIndex = 0;

      for (const level of levels) {
        if (abortControllerRef.current?.signal.aborted) break;
        if (isPausedRef.current) break;

        // Execute all phases at this level sequentially.
        // AgentStreamingService is a singleton — concurrent stream() calls
        // abort previous in-flight requests. Sequential execution also avoids
        // race conditions on accumulatedArtifacts and accumulatedFeedback refs.
        for (const phase of level) {
          if (abortControllerRef.current?.signal.aborted) break;
          if (isPausedRef.current) break;
          await executeAndAuditPhase(phase, phaseIndex++);
        }
      }

      // Final audit
      if (!abortControllerRef.current?.signal.aborted && planStore.roadmap) {
        const finalAuditRoadmap = roadmap;
        advanceStage('final_audit');
        const finalAuditStart = Date.now();
        debugEvent('step_start', { step: 'plan_final_audit', agent: 'PlanAuditService' });

        // Build compact per-phase audit summaries for final audit context
        // Use getState() to read the *current* store — the hook-derived `planStore`
        // snapshot is stale because React hasn't re-rendered during async execution.
        const latestState = usePlanStore.getState();
        const finalAuditReadiness = buildFinalAuditReadiness({
          phases: sorted,
          implementationAudits: latestState.implementationAudits,
          executionErrors: latestState.executionErrors,
        });

        if (!finalAuditReadiness.canRunFinalAudit) {
          const syntheticFinalAudit = buildSyntheticIncompleteFinalAudit(
            finalAuditRoadmap,
            finalAuditReadiness.blockingIssues,
          );
          planStore.setFinalAudit(syntheticFinalAudit);
          debugEvent('quality_gate', {
            stage: 'plan_final_audit',
            verdict: 'fail',
            action: 'skipped',
            reason: 'final audit skipped because one or more phases were blocked or missing implementation audits',
            blockingIssues: finalAuditReadiness.blockingIssues,
          });
          debugEvent('step_complete', {
            step: 'plan_final_audit',
            duration_ms: Date.now() - finalAuditStart,
            score: syntheticFinalAudit.overallScore,
            synthetic: true,
          });
        } else {
          const finalAudit = await planApi.auditFinal(
            allPhaseResults,
            finalAuditRoadmap,
            finalAuditReadiness.phaseAudits,
            abortControllerRef.current?.signal,
            isDebug,
          );
          planStore.incrementLLMCalls();
          captureDebugPrompts(finalAudit, 'final_audit');
          const finalAuditResult = finalAudit.data?.auditResult;
          if (finalAuditResult) {
            const cappedFinalAudit = applyFinalAuditScoreCaps(
              finalAuditResult,
              finalAuditReadiness.phaseAudits,
            );
            planStore.setFinalAudit(cappedFinalAudit);
            debugEvent('step_complete', { step: 'plan_final_audit', duration_ms: Date.now() - finalAuditStart, score: cappedFinalAudit.overallScore });
          }
        }
      }

      // Complete
      planStore.setExecutionTimeMs(Date.now() - executionStartTimeRef.current);
      planStore.setPipelineProgress(100);
      advanceStage('complete');
      planStore.setExecuting(false);
      debug.getState().endPlanDebug();
      debug.getState().endSession();

      // Compute artifact counts
      const counts: Record<string, number> = {};
      for (const domainArts of Object.values(accumulatedArtifacts.current)) {
        for (const [key, ids] of Object.entries(domainArts)) {
          counts[key] = (counts[key] || 0) + ids.length;
        }
      }
      planStore.setArtifactCounts(counts);

      // Add to history
      planStore.addToPlanHistory({
        id: planStore.currentThreadId || crypto.randomUUID(),
        title: planStore.planName || planStore.roadmap?.title || 'Untitled Plan',
        date: new Date().toISOString(),
        auditScore: planStore.finalAudit?.overallScore,
      });
    } catch (err) {
      if (!isAbortError(err)) {
        debugEvent('error', { stage: 'plan_execution', error: toErrMsg(err) });
        debug.getState().endPlanDebug();
        debug.getState().endSession();
        planStore.setError(toErrMsg(err));
        advanceStage('failed');
        planStore.setExecuting(false);
      }
    }
  }, [
    abortControllerRef,
    accumulatedArtifacts,
    accumulatedFeedback,
    advanceStage,
    debug,
    debugEvent,
    executePhase,
    executionStartTimeRef,
    isPausedRef,
    planQualityGate,
    planStore,
  ]);

  // ── executePlan (Quick Mode) ───────────────────────────────────────────────

  const executePlan = useCallback(async () => {
    if (!planStore.plan) return;
    const sections = Array.isArray(planStore.plan.sections) ? planStore.plan.sections : [];
    const sorted = topologicalSort(sections);

    console.info('[Plan:executePlan] Starting quick mode execution', {
      sectionCount: sections.length,
      sortedCount: sorted.length,
      sectionIds: sections.map(s => s.id),
      sortedIds: sorted.map(s => `${s.id} (${s.domain}${s.pageName ? ':' + s.pageName : ''})`),
      editedPhasePromptKeys: Object.keys(usePlanStore.getState().editedPhasePrompts),
    });

    planStore.setExecuting(true);

    // Initialize plan debug for saved-roadmap execution path (bypasses handlePlanMessage)
    if (debug.getState().isEnabled && !debug.getState().planDebugData) {
      debug.getState().initPlanDebug();
    }

    if (!planStore.pipelineStartedAt) {
      planStore.setPipelineStartedAt(Date.now());
    }
    advanceStage('executing_phase');
    executionStartTimeRef.current = Date.now();
    accumulatedArtifacts.current = {};

    try {
      for (let i = 0; i < sorted.length; i++) {
        if (abortControllerRef.current?.signal.aborted) {
          console.warn('[Plan:executePlan] Abort signal detected, breaking at phase', i);
          break;
        }
        const section = sorted[i];
        console.info(`[Plan:executePlan] Phase ${i + 1}/${sorted.length}: ${section.id} (${section.domain})`, {
          pageName: section.pageName,
          promptPreview: section.executionPrompt?.slice(0, 100),
        });
        planStore.setCurrentExecutingPhaseId(section.id);
        planStore.setPipelineProgress(Math.round((i / sorted.length) * 100));
        planStore.updateSectionStatus(section.id, 'executing');

        const sectionStart = Date.now();
        debugEvent('step_start', { step: 'plan_phase_execute', phaseId: section.id, phaseNumber: i + 1, total: sorted.length });

        try {
          const { artifacts } = await executePhase(section.id);
          console.info(`[Plan:executePlan] Phase ${i + 1} completed:`, section.id, { artifacts: Object.keys(artifacts), duration_ms: Date.now() - sectionStart });
          planStore.updateSectionStatus(section.id, 'completed');
          planStore.updateSectionArtifacts(section.id, artifacts);
          debugEvent('step_complete', { step: 'plan_phase_execute', phaseId: section.id, duration_ms: Date.now() - sectionStart });
        } catch (err) {
          console.error(`[Plan:executePlan] Phase ${i + 1} failed:`, section.id, toErrMsg(err));
          debugEvent('error', { stage: 'plan_phase_execute', phaseId: section.id, error: toErrMsg(err) });
          planStore.setExecutionError(section.id, toErrMsg(err));
          planStore.updateSectionStatus(section.id, 'failed');
          // Best-effort: continue on error
        }
      }

      // Complete
      planStore.setExecutionTimeMs(Date.now() - executionStartTimeRef.current);
      planStore.setPipelineProgress(100);
      advanceStage('complete');
      planStore.setExecuting(false);
      debug.getState().endPlanDebug();
      debug.getState().endSession();

      // Compute artifact counts
      const counts: Record<string, number> = {};
      for (const domainArts of Object.values(accumulatedArtifacts.current)) {
        for (const [key, ids] of Object.entries(domainArts)) {
          counts[key] = (counts[key] || 0) + ids.length;
        }
      }
      planStore.setArtifactCounts(counts);
    } catch (err) {
      if (!isAbortError(err)) {
        debugEvent('error', { stage: 'plan_execution', error: toErrMsg(err) });
        debug.getState().endPlanDebug();
        debug.getState().endSession();
        planStore.setError(toErrMsg(err));
        advanceStage('failed');
        planStore.setExecuting(false);
      }
    }
  }, [
    abortControllerRef,
    accumulatedArtifacts,
    advanceStage,
    debug,
    debugEvent,
    executePhase,
    executionStartTimeRef,
    planStore,
  ]);

  // ── pauseExecution ─────────────────────────────────────────────────────────

  const pauseExecution = useCallback(() => {
    isPausedRef.current = true;
  }, [isPausedRef]);

  // ── retryFromStage (G-6) ──────────────────────────────────────────────────

  const retryFromStage = useCallback(async (stage: PlanPipelineStage) => {
    planStore.setError(null);
    const controller = new AbortController();
    abortControllerRef.current = controller;
    planStore.setAbortController(controller);

    switch (stage) {
      case 'researching':
        advanceStage('researching');
        // runFullModePipeline is defined in the parent — call advanceToPhaseDecomposition
        // for stages that only need decomposition, otherwise parent handles full pipeline.
        break;
      case 'generating_roadmap':
        advanceStage('generating_roadmap');
        break;
      case 'decomposing':
        await advanceToPhaseDecomposition();
        break;
      case 'executing_phase':
        if (planStore.pipelineMode === 'full') {
          await executeAllPhases();
        } else {
          await executePlan();
        }
        break;
    }
  }, [abortControllerRef, advanceStage, advanceToPhaseDecomposition, executeAllPhases, executePlan, planStore]);

  return {
    advanceToPhaseDecomposition,
    executePhase,
    executeAllPhases,
    executePlan,
    pauseExecution,
    retryFromStage,
  };
}
